import React, { useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import classNames from 'classnames';
import _ from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import voca from 'voca';

import {
  useCreateProjectArchiveMutation,
  useCreateProjectRestorationMutation,
} from 'components/hooks/projectMutations';
import useDebounce from 'components/hooks/useDebounce';
import useForm from 'components/hooks/useForm';

import TextButton from 'components/application/buttons/TextButton';
import Paginator from 'components/application/Paginator';
import SearchField from 'components/application/SearchField';
import SuccessToast from 'components/application/SuccessToast';
import DotSkeleton from 'components/application/tableSkeletons/DotSkeleton';
import TextSkeleton from 'components/application/tableSkeletons/TextSkeleton';
import UndoableToast from 'components/application/UndoableToast';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useProjectsDashboardContext } from 'components/contexts/ProjectsDashboardContext';
import { useBreadBoard } from 'components/contexts/Toaster';
import LifecycleStatusIndicator from 'components/projects/components/LifecycleStatusIndicator';
import EmptyTableState from 'components/projectsDashboard/components/EmptyTableState';
import { pluralizeProjectsText } from 'components/projectsDashboard/helpers';

import DownArrowIcon from '-!svg-react-loader?name=DownArrowIcon!icons/down-arrow.svg';
import InventoryIcon from '-!svg-react-loader?name=InventoryIcon!icons/inventory.svg';
import SearchOffIcon from '-!svg-react-loader?name=SearchOffIcon!icons/search-off.svg';
import ThreeDotsIcon from '-!svg-react-loader?name=ThreeDotsIcon!icons/three-dots.svg';

import { formatData } from 'components/helpers/reactQuery';
import { resourcesAsResourceMap } from 'components/helpers/serialisableResources';

export default function ArchivedProjectsIndex({ onCurrentSectionClick }) {
  const breadBoard = useBreadBoard();
  const currentActor = useCurrentActor();

  const singularProjectName =
    currentActor.division.attributes.singularRAMSName || 'RAMS';
  const pluralProjectName =
    currentActor.division.attributes.pluralRAMSName || 'RAMS';

  const { folderPolicy } = useProjectsDashboardContext();

  const [currentPage, setCurrentPage] = useState(1);
  const [currentSearch, setCurrentSearch, handleCurrentSearchInputChange] =
    useForm({
      archiveSearch: '',
    });
  const [debouncedCurrentSearch] = useDebounce(currentSearch, 250, {
    onChange: () => setCurrentPage(1),
  });

  const projectReportParams = {
    filter: { archived: { eq: true } },
    page: currentPage,
    search: debouncedCurrentSearch.archiveSearch,
    selections: folderPolicy.canIndex ? ['folder', 'live'] : ['live'],
    sort: { column: 'archived_at', direction: 'applied' },
  };

  const {
    data: domainProjects,
    isError: isDomainProjectsFetchError,
    isFetching: isDomainProjectsFetching,
    isLoading: isDomainProjectsLoading,
    isSuccess: isDomainProjectsFetchSuccess,
  } = useQuery({
    queryKey: ['projectReports', projectReportParams],
    queryFn: async () => {
      const response = await axios.post('/dashboards/project_reports', {
        project_report: projectReportParams,
      });

      return response.data;
    },
    keepPreviousData: true,
    select: (data) => formatData({ data, resourceTypes: ['folder'] }),
    onError: breadBoard.addInedibleToast,
  });

  const buildColumnsArgs = { folderPolicy, singularProjectName };
  const columns = useMemo(
    () => buildColumns(buildColumnsArgs),
    Object.values(buildColumnsArgs),
  );

  const isDataPending =
    isDomainProjectsLoading ||
    isDomainProjectsFetching ||
    isDomainProjectsFetchError;

  const table = useReactTable({
    columns,
    data:
      isDataPending ?
        Array(domainProjects?.data.length || 25).fill({})
      : domainProjects?.data || [],
    getCoreRowModel: getCoreRowModel(),
    meta: {
      isDataPending,
      included: domainProjects?.included || {},
    },
  });

  const isSearchFieldDisabled =
    isDomainProjectsFetchSuccess &&
    !debouncedCurrentSearch.archiveSearch &&
    domainProjects.data.length === 0;

  return (
    <div className='tw-flex tw-flex-col tw-gap-6'>
      <div className='tw-flex tw-flex-col tw-gap-1 tw-px-4'>
        <div className='tw-flex tw-h-10 tw-items-center tw-justify-between'>
          <div className='tw-flex tw-items-center tw-gap-1'>
            <InventoryIcon
              className='[&_path]:tw-fill-grey-500'
              height={20}
              width={20}
            />
            <p className='tw-mb-0 tw-px-1.5 tw-text-xl tw-font-semibold tw-tracking-tight tw-text-grey-900'>
              Archive
            </p>
          </div>
        </div>
        <SearchField
          additionalClasses={classNames(
            'tw-w-70',
            isSearchFieldDisabled ? 'tw-bg-grey-025' : 'tw-bg-white',
          )}
          isDisabled={isSearchFieldDisabled}
          name='archiveSearch'
          onChange={handleCurrentSearchInputChange}
          onReset={() => {
            setCurrentSearch({ ...currentSearch, archiveSearch: '' });
          }}
          placeholder='Search archive...'
          value={currentSearch.archiveSearch}
        />
      </div>
      {(
        isDomainProjectsFetchSuccess &&
        !debouncedCurrentSearch.archiveSearch &&
        domainProjects.data.length === 0
      ) ?
        <EmptyTableState
          icon={InventoryIcon}
          title={`You have no archived ${pluralProjectName}`}
        >
          <p className='tw-mb-0 tw-text-center tw-text-m tw-font-normal tw-text-grey-900'>
            Your archived {pluralProjectName} will appear here
          </p>
        </EmptyTableState>
      : <div className='tw-flex tw-flex-col tw-gap-2'>
          <div className='tw-flex tw-h-8 tw-items-center tw-px-4'>
            <p className='tw-mb-0 tw-text-l tw-font-semibold tw-tracking-auto tw-text-grey-700'>
              {pluralizeProjectsText({
                count:
                  isDomainProjectsFetchSuccess ?
                    domainProjects.meta.scopedCount
                  : 0,
                currentActor,
              })}
            </p>
          </div>
          {isDataPending || domainProjects.data.length > 0 ?
            <>
              <table className='tw-table-fixed tw-border-separate tw-select-none tw-border-1 tw-border-l-0 tw-border-r-0 tw-border-solid tw-border-grey-100 tw-bg-white'>
                <thead className='tw-bg-grey-050'>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <th
                          className={header.column.columnDef.meta?.classNames}
                          key={header.id}
                        >
                          <div
                            className={classNames(
                              'tw-flex tw-h-10 tw-items-center tw-justify-start tw-truncate tw-p-2 tw-text-s tw-font-medium tw-tracking-auto tw-text-grey-900',
                              header.column.getIsFirstColumn() ?
                                'tw-pl-4 tw-pr-2'
                              : 'tw-px-2',
                            )}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map((row) => (
                    <tr
                      className='tw-group/row tw-bg-white hover:tw-bg-grey-025 has-[button:hover]:tw-bg-blue-025'
                      key={row.id}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td
                          className={classNames(
                            'tw-h-13 tw-border-0 tw-border-t-1 tw-border-solid tw-border-grey-100',
                            cell.column.columnDef.meta?.classNames,
                          )}
                          key={cell.id}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {isDomainProjectsFetchSuccess &&
                domainProjects.meta.totalPages > 1 && (
                  <div className='tw-mt-12 tw-text-center'>
                    <Paginator
                      currentPage={domainProjects.meta.currentPage}
                      onClick={(event) =>
                        setCurrentPage(
                          Number(event.currentTarget.getAttribute('data-page')),
                        )
                      }
                      totalPages={domainProjects.meta.totalPages}
                    />
                  </div>
                )}
            </>
          : <EmptyTableState
              icon={SearchOffIcon}
              title={`No ${pluralProjectName} found`}
            >
              <p className='tw-mb-0 tw-text-center tw-text-m tw-font-normal tw-text-grey-900'>
                Try searching again by {singularProjectName} name, client or
                reference, or search{' '}
                <button
                  className='tw-border-0 tw-bg-transparent tw-p-0 tw-text-m tw-font-medium tw-text-blue-500'
                  onClick={onCurrentSectionClick}
                >
                  current {pluralProjectName}
                </button>
              </p>
            </EmptyTableState>
          }
        </div>
      }
    </div>
  );
}

ArchivedProjectsIndex.propTypes = {
  onCurrentSectionClick: PropTypes.func.isRequired,
};

function buildColumns({ folderPolicy, singularProjectName }) {
  const columns = [
    {
      id: 'live',
      header: 'Live',
      cell: ({ row, table }) => {
        if (table.options.meta.isDataPending)
          return (
            <div className='tw-pl-4 tw-pr-2'>
              <DotSkeleton />
            </div>
          );

        const status = _.camelCase(row.original.meta.calculatedLifecycleStatus);

        return (
          <div className='tw-flex tw-h-full tw-items-center tw-justify-center tw-pl-4 tw-pr-2'>
            <LifecycleStatusIndicator
              ramsSingularName={singularProjectName}
              status={status}
            />
          </div>
        );
      },
      meta: { classNames: 'tw-w-12' },
    },
    {
      id: 'project',
      header: voca.capitalize(singularProjectName),
      cell: ({ row, table }) => {
        if (table.options.meta.isDataPending) return <TextSkeleton />;

        return (
          <div className='tw-h-full tw-content-center tw-truncate tw-px-2 tw-text-m tw-font-medium tw-tracking-auto tw-text-grey-900'>
            {row.original.attributes.name}
          </div>
        );
      },
    },
    {
      id: 'folder',
      header: 'Folder',
      cell: ({ row, table }) => {
        if (table.options.meta.isDataPending) return <TextSkeleton />;

        const folder =
          table.options.meta.included.folder[
            row.original.relationships.folder?.data?.id
          ];

        return (
          <div className='tw-h-full tw-content-center tw-truncate tw-px-2 tw-text-m tw-font-normal tw-tracking-auto tw-text-grey-700'>
            {folder?.attributes.name}
          </div>
        );
      },
      meta: { classNames: 'tw-w-60' },
    },
    {
      id: 'reference',
      header: 'Reference',
      cell: ({ row, table }) => {
        if (table.options.meta.isDataPending) return <TextSkeleton />;

        return (
          <div className='tw-h-full tw-content-center tw-truncate tw-px-2 tw-text-m tw-font-normal tw-tracking-auto tw-text-grey-700'>
            {row.original.attributes.reference}
          </div>
        );
      },
      meta: { classNames: 'tw-w-30' },
    },
    {
      id: 'archivedAt',
      header: () => (
        <div className='tw-flex tw-items-center tw-gap-1'>
          Archived
          <DownArrowIcon
            className='[&_path]:tw-fill-grey-300'
            height={20}
            width={20}
          />
        </div>
      ),
      cell: ({ row, table }) => {
        if (table.options.meta.isDataPending) return <TextSkeleton />;

        return (
          <div className='tw-h-full tw-content-center tw-truncate tw-px-2 tw-text-m tw-font-normal tw-tracking-auto tw-text-grey-700'>
            {moment(row.original.attributes.archivedAt).format('DD MMM YY')}
          </div>
        );
      },
      meta: { classNames: 'tw-w-30' },
    },
    {
      id: 'actions',
      header: '',
      accessorFn: (project) => project.id,
      cell: ({ row }) => (
        <ArchivedProjectsTableRowActions projectId={row.original.id} />
      ),
      meta: { classNames: 'tw-w-23' },
    },
  ];

  if (folderPolicy.canIndex) return columns;

  return columns.filter((column) => column.id !== 'folder');
}

function ArchivedProjectsTableRowActions({ projectId }) {
  const breadBoard = useBreadBoard();

  const createProjectArchiveMutation = useCreateProjectArchiveMutation();
  const createProjectRestorationMutation =
    useCreateProjectRestorationMutation();

  const handleRestore = ({ id }) => {
    createProjectRestorationMutation.mutate(
      { id },
      {
        onSuccess: (data) => {
          const resourceMap = resourcesAsResourceMap({
            resources: data.included,
            resourceTypes: ['folder', 'project'],
          });

          const project =
            resourceMap.project[data.data.relationships.project.data.id];

          const folder =
            resourceMap.folder[project.relationships.folder.data?.id];

          breadBoard.addToast(
            <UndoableToast
              isUndoActionDisabled={createProjectArchiveMutation.isLoading}
              onBurnToast={breadBoard.handleBurnToast}
              onUndoAction={() =>
                handleRestoreUndo({
                  id: project.id,
                })
              }
              toastText={
                <>
                  <span className='tw-font-semibold'>
                    {project.attributes.name}
                  </span>{' '}
                  was restored
                  {!!folder && (
                    <>
                      {' '}
                      to folder{' '}
                      <span className='tw-font-semibold'>
                        {folder.attributes.name}
                      </span>
                    </>
                  )}
                </>
              }
            />,
          );
        },
        onError: breadBoard.addInedibleToast,
      },
    );
  };

  const handleRestoreUndo = ({ id }) => {
    createProjectArchiveMutation.mutate(
      { id },
      {
        onSuccess: (data) => {
          const resourceMap = resourcesAsResourceMap({
            resources: data.included,
            resourceTypes: ['project'],
          });

          const project =
            resourceMap.project[data.data.relationships.project.data.id];

          breadBoard.addToast(
            <SuccessToast
              message={
                <>
                  <span className='tw-font-semibold'>
                    {project.attributes.name}
                  </span>{' '}
                  not restored
                </>
              }
              onBurnToast={breadBoard.handleBurnToast}
            />,
          );
        },
        onError: breadBoard.addInedibleToast,
      },
    );
  };

  return (
    <div className='tw-flex tw-items-center tw-justify-center'>
      <ThreeDotsIcon
        className='group-hover/row:tw-hidden [&_path]:tw-fill-grey-500'
        width={12}
      />
      <TextButton
        className='tw-hidden hover:tw-bg-blue-050 group-hover/row:tw-inline-flex'
        color='blue'
        disabled={createProjectRestorationMutation.isLoading}
        onClick={() => {
          handleRestore({ id: projectId });
        }}
        size='sm'
      >
        Restore
      </TextButton>
    </div>
  );
}
